/**
 * Parsed slide-deck-comparison payload model.
 *
 * DECISION (Phase 4, RESEARCH Open Question #1): the parsed comparison `payload` is modelled
 * with snake_case interfaces that match the verbatim upstream JSON 1:1 — NO snake->camel mapper.
 * This honors the PROJECT constraint "minimal transformation between ingest and frontend; raw
 * JSON stored verbatim" and removes a mapping layer that could silently drift from the payload.
 *
 * All payload fields are treated as nullable (RESEARCH Assumption A1): `company_id` is null in
 * every sample, `title` is frequently null, and `match_score`/`old_slide_number`/`new_slide_number`
 * are null for ADDED/REMOVED pairs.
 */

/**
 * A single region highlight on a slide image; all coordinates are fractions in the range 0..1
 * relative to the slide dimensions, scoped to either the OLD or NEW slide.
 */
export interface Region {
  slide_type: 'OLD' | 'NEW' | null;
  x_coordinate: number | null;
  y_coordinate: number | null;
  width: number | null;
  height: number | null;
}

/**
 * A single detected difference between an old and new slide, with optional highlight regions.
 *
 * Used by the TOP_DOWN view only. SIDE_BY_SIDE moved to the box-centric {@link SlideBox} model;
 * `element_type` / `isHTML` were added here additively (TOP_DOWN `isHTML` is always false).
 */
export interface Difference {
  status: 'CHANGED' | 'ADDED' | 'REMOVED' | null;
  title: string | null;
  element_type?: 'TEXT' | 'TABLE' | 'IMAGE' | 'OTHER' | null;
  isHTML?: boolean | null;
  old_description: string | null;
  new_description: string | null;
  change_summary: string | null;
  match_score: number | null;
  regions: Array<Region> | null;
}

/**
 * A single field-level before->after delta inside a {@link BoxChange} (a table/card cell change).
 * Rendered as a `field: before -> after` row. A null `before` denotes an added field; a null `after`
 * denotes a removed field.
 */
export interface FieldChange {
  field: string | null;
  before: string | null;
  after: string | null;
}

/**
 * One change inside a SIDE_BY_SIDE {@link SlideBox}. Self-contained: it carries its own before/after.
 * A `modified` change appears as a record under BOTH its OLD and NEW box, linked across them by the
 * same `group`. When `field_changes` is non-empty it is the source of truth (and `old_description` /
 * `new_description` are null); otherwise the description fields drive the textual diff.
 */
export interface BoxChange {
  status: 'CHANGED' | 'ADDED' | 'REMOVED' | null;
  group: string | null;
  theme: string | null;
  field_changes: Array<FieldChange> | null;
  old_description: string | null;
  new_description: string | null;
  change_summary: string | null;
}

/**
 * The normalized 0..1 rectangle of a SIDE_BY_SIDE {@link SlideBox} (origin top-left). Null on the box
 * means no element resolved — render a whole-slide highlight.
 */
export interface BoxGeometry {
  x_coordinate: number | null;
  y_coordinate: number | null;
  width: number | null;
  height: number | null;
}

/**
 * A box-centric SIDE_BY_SIDE element: one distinct slide element the engine resolved, carrying every
 * change inside it as a self-contained `changes[]` record. Exactly one rectangle is drawn per box
 * (its `geometry`) on the `slide_type` side; `status` is the coarse box color and `changes[]` feeds
 * the textual diff. `isHTML` is true when a change carries extracted `<table>` markup to render.
 */
export interface SlideBox {
  box_id: string | null;
  slide_type: 'OLD' | 'NEW' | null;
  geometry: BoxGeometry | null;
  element_type: 'TEXT' | 'TABLE' | 'IMAGE' | 'OTHER' | null;
  isHTML: boolean | null;
  status: 'CHANGED' | 'ADDED' | 'REMOVED' | null;
  changes: Array<BoxChange> | null;
}

/**
 * A flattened, group-deduped SIDE_BY_SIDE change ready for rendering. Built once by the pair card from
 * a pair's `boxes[]` and shared with the hosted diff so the rectangle `box-num` and the diff `diff-num`
 * use the SAME number for the SAME logical change (cross-highlight keyed by `diffId`/`group`). `num` is
 * 1-based in first-appearance order; `isHTML` / `elementType` are inherited from the owning box.
 */
export interface NormalizedChange {
  group: string;
  num: number;
  diffId: string;
  status: 'CHANGED' | 'ADDED' | 'REMOVED' | null;
  theme: string | null;
  fieldChanges: Array<FieldChange>;
  oldDescription: string | null;
  newDescription: string | null;
  changeSummary: string | null;
  isHTML: boolean;
  elementType: 'TEXT' | 'TABLE' | 'IMAGE' | 'OTHER' | null;
}

/**
 * A matched (or unmatched) pair of slides between the old and new deck, with its diff set.
 *
 * `title` / `summary` are TOP_DOWN-only: the key is present on TOP_DOWN slide_pairs (matched-pair
 * label / consolidated single-evidence card text) and ABSENT on SIDE_BY_SIDE slide_pairs (both were
 * always null there and were dropped from the payload — the SIDE_BY_SIDE top-of-view summary now
 * lives in {@link ComparisonDetail.summary_bullets}). Kept optional so a SIDE_BY_SIDE pair simply
 * reads `undefined`.
 */
export interface SlidePair {
  old_slide_number: number | null;
  new_slide_number: number | null;
  title?: string | null;
  summary?: string | null;
  status: 'UNCHANGED' | 'CHANGED' | 'ADDED' | 'REMOVED' | null;
  match_score: number | null;
  detection_failed: boolean | null;
  skip_reason: string | null;
  /** SIDE_BY_SIDE: the box-centric element list (one rectangle per box). Null/absent in TOP_DOWN. */
  boxes?: Array<SlideBox> | null;
  /** TOP_DOWN: the analysis-point list. Null/absent in SIDE_BY_SIDE (which uses `boxes`). */
  differences?: Array<Difference> | null;
}

/**
 * A logical grouping of slide pairs within a comparison detail. `theme` is a TOP_DOWN per-finding tag
 * (null for the single default SIDE_BY_SIDE group, where the theme tag lives per-change instead).
 */
export interface SlidePairGroup {
  title: string | null;
  label: string | null;
  summary: string | null;
  theme?: string | null;
  slide_pairs: Array<SlidePair> | null;
}

/**
 * One comparison view (SIDE_BY_SIDE or TOP_DOWN) of the same underlying decks.
 *
 * `summary_bullets` is the top-of-view summary: concise one-line bullets of the biggest
 * valuation-driving changes, most-impactful first. It is generated once by the TOP_DOWN engine and is
 * therefore IDENTICAL across both views of a comparison. It replaces the former per-view prose
 * `summary` string (removed 2026-06-25). May be empty/null when no material change was detected.
 */
export interface ComparisonDetail {
  comparison_type: 'SIDE_BY_SIDE' | 'TOP_DOWN' | null;
  summary_bullets: Array<string> | null;
  slide_pair_groups: Array<SlidePairGroup> | null;
}

/**
 * A reference to a single slide image within a deck.
 */
export interface SlideRef {
  slide_number: number | null;
  s3_reference: string | null;
}

/**
 * Metadata for one side (old or new) of the comparison. `display_name` (RA-2734) is TARA's finished,
 * per-company-unique deck name and is the field to render — read it through `resolveDeckDisplayName`, or on
 * the report page through its resource-first `resolveDeckLabel`, which falls back to composing `date_label`
 * and `subject` via {@link combineDeckLabel} when no name is present. `subject` (RA-2079) is the deck's
 * free-text series/subject tag (e.g. "Investor Day"); it is now part of that fallback rather than the
 * primary rule. `filename` (RA-2079) is the upstream source filename (e.g. "ASND_2025-01_deck.pdf") — used
 * as the tooltip for the display label, since the label alone does not always uniquely identify the file.
 */
export interface SlideDeck {
  date_label: string | null;
  /**
   * TARA's finished display name, rendered verbatim (RA-2734). Optional rather than nullable because TARA
   * omits the key entirely when its naming fails, rather than sending null. A comparison ingested before
   * RA-2734 has no value here even if its deck row has since been named — which is why the report page
   * resolves the resource first.
   */
  display_name?: string | null;
  subject: string | null;
  filename: string | null;
  deck_type: string | null;
  slide_count: number | null;
  s3_reference: string | null;
  slides: Array<SlideRef> | null;
}

/**
 * The full verbatim comparison payload as produced upstream and stored in RMS.
 */
export interface ComparisonPayload {
  comparison_id: string | null;
  company_id: number | null;
  company_name: string | null;
  company_ticker: string | null;
  old_slide_deck: SlideDeck | null;
  new_slide_deck: SlideDeck | null;
  generated_at: string | null;
  same_company: boolean | null;
  same_deck_type: boolean | null;
  /**
   * The deck-level share of slides that pair between the two decks, a 0–1 fraction. This is the field the backend
   * extracts into the `matchRate` scalar column that drives the Decks modal's MATCH column; nothing reads it off
   * the payload here.
   */
  slide_match_rate: number | null;
  /**
   * A per-slide match threshold, superseded by {@link ComparisonPayload.slide_match_rate} as the source of the
   * MATCH column. Still published upstream, so it is still declared here for payload fidelity — but read by
   * nothing in RMS.
   */
  min_slide_match: number | null;
  details: Array<ComparisonDetail> | null;
}

/**
 * One persisted physical slide deck, returned by the backend as a normalized row (RA-2079). This is
 * the read-endpoint projection — distinct from the verbatim payload's {@link SlideDeck} (which carries
 * the per-slide image refs). A deck no longer carries a single OLD/NEW status or a single owning
 * comparison — a physical deck (identified by the producer-supplied `slideDeckId`) can recur across
 * multiple comparisons in a lineage thread. When nested under a comparison, this resource is wrapped in
 * a {@link SlideDeckLinkResource} that carries that link's own `deckStatus`.
 */
export interface SlideDeckResource {
  id: number;
  slideDeckId: string | null;
  s3Reference: string | null;
  filename: string | null;
  deckType: string | null;
  subject: string | null;
  slideCount: number | null;
  date: string | null;
  dateLabel: string | null;
  /**
   * TARA's finished display name, rendered verbatim (RA-2734). Unique across the company's decks via a
   * " (n)" suffix TARA assigns at registration. Null for a legacy or unnamed deck, where the UI falls
   * back to composing `dateLabel` + `subject`; read it through `resolveDeckDisplayName`, never directly.
   */
  displayName: string | null;
  created: string | null;
  /**
   * When the deck became available in RMS — the value shown as "Uploaded", ordered by, and used for the
   * `New` badge (RA-2731). Equal to `created` unless the deck has been deleted and restored; `created` is
   * audit-only from that ticket on and nothing in the frontend reads it.
   */
  uploaded: string | null;
  /** The owning company's RMS id; null when the deck's company has not been resolved (RA-2255). */
  companyId: number | null;
  /** The company ticker as submitted by the producer, retained for diagnosis (RA-2255). */
  companyTicker: string | null;
  /**
   * The RMS id of the analyst who uploaded this deck through self service (RA-2251), or null when the deck came
   * from TARA's automated harvest. The Decks table matches this against the signed-in user's id — never against
   * the name strings, which collide for two people sharing a name.
   */
  uploadedByUserId: number | null;
  /** The uploading analyst's first name; null (together with {@link uploadedByLastName}) for a harvested deck. */
  uploadedByFirstName: string | null;
  /** The uploading analyst's last name; see {@link uploadedByFirstName}. */
  uploadedByLastName: string | null;
}

/**
 * One OLD/NEW slot in a comparison's deck pair: the link's own status plus the linked physical deck's
 * own fields (RA-2079's deck↔comparison refactor made decks shareable across comparisons via a join
 * entity on the backend).
 */
export interface SlideDeckLinkResource {
  id: number;
  deckStatus: 'OLD' | 'NEW' | null;
  slideDeck: SlideDeckResource;
}

/**
 * The 6 "comparable" deck types (Dan's classifier taxonomy, DECK-TYPE-SERIES-SPEC): a deck of one of
 * these types is ever paired into a comparison. Order matches the canonical display order used by
 * every deck-type tab bar in this feature (both Decks and Comparisons).
 */
export const COMPARABLE_DECK_TYPES: ReadonlyArray<string> = [
  'corporate_presentation', 'earnings', 'conference', 'rd_day', 'clinical_data', 'scientific'
];

/**
 * The "deck-only" deck types: a deck of one of these types is never paired into a comparison, so they
 * appear on the Decks tab bar only (never on the Comparisons tab bar).
 */
export const OTHER_DECK_TYPE = 'other';
export const DEAL_ANNOUNCEMENT_DECK_TYPE = 'deal_announcement';
export const DECKS_ONLY_DECK_TYPES: ReadonlyArray<string> = [DEAL_ANNOUNCEMENT_DECK_TYPE, OTHER_DECK_TYPE];

/**
 * The full Decks-tab-bar taxonomy: the 6 comparable types followed by the 2 deck-only types. The
 * Comparisons tab bar uses {@link COMPARABLE_DECK_TYPES} alone. `deck_type` remains a free-form backend
 * string — this taxonomy is a frontend-only canonicalization for tab display/ordering; a `deckType`
 * value outside this list still renders (humanized) on its row, it just never gets its own tab.
 */
export const DECK_TYPE_TAXONOMY: ReadonlyArray<string> = [...COMPARABLE_DECK_TYPES, ...DECKS_ONLY_DECK_TYPES];

/**
 * The backend read-endpoint wrapper around a stored comparison: extracted/indexed camelCase
 * scalar columns plus the verbatim `payload`. The backend annotates the payload `@JsonRawValue`, so
 * Jackson emits it as embedded JSON (a real object) rather than a double-encoded string; Angular's
 * HttpClient parses the response body, so `payload` arrives already-parsed as a `ComparisonPayload`
 * object (it must NOT be `JSON.parse`d again). The OLD/NEW decks are also returned as normalized
 * `slideDecks` rows, each wrapped in a {@link SlideDeckLinkResource} (RA-2079); the report page itself
 * renders from `payload`.
 */
export interface SlideDeckComparisonResource {
  id: number;
  payload: ComparisonPayload;
  comparisonId: string | null;
  companyId: number | null;
  companyName: string | null;
  companyTicker: string | null;
  deckType: string | null;
  subject: string | null;
  generatedAt: string | null;
  status: string | null;
  /**
   * The comma-delimited list of views this comparison offers (e.g. `"SIDE_BY_SIDE,TOP_DOWN"` or just
   * `"TOP_DOWN"`), populated by the backend at ingestion. The report page uses it to gate the header
   * view toggle and redirect away from a `view` query param requesting an unavailable view.
   */
  comparisonTypes: string | null;
  slideDecks: Array<SlideDeckLinkResource> | null;
  /** True when an analyst requested this comparison through self service (RA-2251); see {@link SlideDeckComparisonRequest}. */
  manuallyGenerated: boolean | null;
  /** The requesting analyst's first name for a manually-generated comparison; null otherwise (never their email). */
  requestedByFirstName: string | null;
  /** The requesting analyst's last name for a manually-generated comparison; null otherwise. */
  requestedByLastName: string | null;
}

/**
 * The metadata-only projection of a stored comparison, returned by the list endpoint
 * (`GET /api/rms/slide-deck-comparisons?include_payload=false`). It omits the heavy verbatim
 * `payload` for performance and instead carries the indexed camelCase scalar columns plus:
 *
 * - `slideDecks`: the comparison's OLD/NEW decks as normalized {@link SlideDeckLinkResource} rows
 *   (RA-2079). The Comparisons table reads the OLD and NEW `dateLabel`s from here to render the
 *   "old -> new" lineage; this replaced the old flat `oldDeckLabel` / `newDeckLabel` columns.
 * - `created`: the comparison record's creation timestamp (the base audit column).
 * - `comparisonTypes`: a comma-delimited list of the views available for this comparison
 *   (e.g. `"SIDE_BY_SIDE,TOP_DOWN"`), driving the per-row view buttons.
 */
export interface SlideDeckComparisonMeta {
  id: number;
  comparisonId: string | null;
  companyId: number | null;
  companyName: string | null;
  companyTicker: string | null;
  deckType: string | null;
  subject: string | null;
  generatedAt: string | null;
  created: string | null;
  status: string | null;
  comparisonTypes: string | null;
  slideDecks: Array<SlideDeckLinkResource> | null;
  /** True when an analyst requested this comparison through self service (RA-2251). */
  manuallyGenerated: boolean | null;
  /** The requesting analyst's first name for a manually-generated comparison; null otherwise. */
  requestedByFirstName: string | null;
  /** The requesting analyst's last name for a manually-generated comparison; null otherwise. */
  requestedByLastName: string | null;
  /**
   * The share of slides that pair between the two decks, read verbatim from the payload's `slide_match_rate`
   * (the backend performs no arithmetic on it). Null for a comparison whose payload never stated one. It is a
   * 0–1 fraction, and every read still goes through the Decks modal's `toMatchPercent` helper rather than being
   * rendered directly, so the unit lives in one place — see the frontend spec §6.3.
   */
  matchRate: number | null;
}

/**
 * A self-service slide-deck comparison request (RA-2251): an analyst picked two decks from a company page and
 * RMS asked the TARA service to generate the comparison. Generation takes minutes, so a `PENDING` request is
 * rendered as a pinned "Generating…" placeholder above the company's comparison list until the finished
 * comparison arrives through the existing ingest endpoint and the request flips to `COMPLETE`.
 *
 * `stale` is derived by the backend at read time (a `PENDING` request past a configured TTL) and is never
 * stored — nothing sweeps a request that TARA accepted but never returned, so a stale row simply reads as
 * "taking longer than expected" from then on.
 *
 * Every field is camelCase: this is an RMS resource, not part of the verbatim snake_case AI payload.
 */
export interface SlideDeckComparisonRequest {
  id: number;
  requestId: string | null;
  companyId: number | null;
  status: SlideDeckComparisonRequestStatus | null;
  requestedByFirstName: string | null;
  requestedByLastName: string | null;
  slideDeckComparisonId: number | null;
  completed: string | null;
  stale: boolean | null;
  /**
   * Why a `FAILED` request failed, as a bounded token from the upstream pipeline; null for every other status.
   * Treated defensively — this is a cross-team enum that can gain a member without an RMS release.
   */
  failureReason: string | null;
  /**
   * TARA's own sanitized explanation, present only when `failureReason` is a token RMS has no authored copy
   * for (RA-2733). Null on success, and null whenever the token DOES have copy — the authored string wins,
   * because it is translatable and on register and TARA's prose is neither.
   *
   * Rendered as TEXT through interpolation, never as markup: it is third-party free text. The backend
   * sanitizes and length-caps it on write; binding it through `{{ }}` is the second layer.
   */
  failureDetail: string | null;
  /**
   * The OLD side's deck. Both sides are always existing decks now that self service compares two decks RMS
   * already holds (uploads are their own {@link SlideDeckRequest} flow), so neither side is ever an
   * unnamed upload in flight.
   */
  oldSlideDeck: SlideDeckResource;
  /** The NEW side's deck; see {@link oldSlideDeck}. */
  newSlideDeck: SlideDeckResource;
}

/** The lifecycle states a self-service deck upload request moves through (RA-2251). */
export type SlideDeckRequestStatus = 'PENDING' | 'COMPLETE' | 'DUPLICATE' | 'REJECTED' | 'FAILED';

/** The lifecycle states a self-service comparison request moves through (RA-2251). */
export type SlideDeckComparisonRequestStatus = 'PENDING' | 'COMPLETE' | 'FAILED';

/**
 * A self-service slide-deck **upload** request (RA-2251): an analyst uploaded one PDF from the Deck Comparisons
 * modal and RMS relayed the bytes to TARA, which extracts the deck's date, category and slide count. The request
 * is rendered as a pinned "Uploading…" row at the top of the decks table until it reaches a terminal status.
 *
 * A terminal status may already be set on the `201` that creates the request — TARA answers a refusal
 * (`REJECTED`, with a {@link failureReason}) or an already-held deck (`DUPLICATE`) inline — so the caller must
 * branch on `status` rather than assuming `PENDING`.
 *
 * Name fields are flat, matching {@link SlideDeckComparisonRequest}; there is deliberately no nested user object.
 * Every field is camelCase: this is an RMS resource, not part of the verbatim snake_case AI payload.
 */
export interface SlideDeckRequest {
  id: number;
  requestId: string | null;
  companyId: number | null;
  /** The filename the analyst uploaded, retained verbatim — the only label available while the deck has no row. */
  originalFilename: string | null;
  fileSizeBytes: number | null;
  status: SlideDeckRequestStatus | null;
  /**
   * Why a `REJECTED` / `FAILED` upload was refused, as a bounded token (e.g. `NOT_A_PDF`, `ENCRYPTED`); null
   * otherwise. Mapped to gate copy defensively — an unrecognised token falls through to `failureDetail`
   * and then to the generic failure copy.
   */
  failureReason: string | null;
  /**
   * TARA's own sanitized explanation, present only when `failureReason` is a token RMS has no authored copy
   * for (RA-2733). Null on success, and null whenever the token DOES have copy — the authored string wins,
   * because it is translatable and on register and TARA's prose is neither.
   *
   * Rendered as TEXT through interpolation, never as markup: it is third-party free text. The backend
   * sanitizes and length-caps it on write; binding it through `{{ }}` is the second layer.
   */
  failureDetail: string | null;
  /**
   * True when the backend judges a still-`PENDING` request past its TTL. Read by exactly one consumer: the
   * modal's open-time reconstruction, which skips a stale request so an abandoned upload cannot pin a spinner
   * on the company forever.
   */
  stale: boolean | null;
  requestedByFirstName: string | null;
  requestedByLastName: string | null;
  /** The deck TARA produced, present once the request completes; null while it is still in flight or refused. */
  slideDeck: SlideDeckResource | null;
  /**
   * The company that already holds this deck's content hash, when `failureReason` is `CROSS_COMPANY`
   * (RA-2726). Both null otherwise, and also on a cross-company refusal against a deck with no company —
   * which falls back to the unnamed copy.
   */
  conflictCompanyId: number | null;
  conflictCompanyName: string | null;
  /**
   * Set only on the `201` of a revival; its presence is the signal that the upload restored a deck
   * (RA-2731). A status poll or a list read never carries it, by design — the only consumer is the response
   * the uploading analyst is already looking at.
   */
  revival: SlideDeckRevival | null;
  created: string | null;
  completed: string | null;
}

/**
 * The restore detail on the `201` of an upload that revived a deleted deck (RA-2731). Present on that
 * response ONLY — a poll or a list read never carries it, by design.
 */
export interface SlideDeckRevival {
  /** How many comparisons came back with the deck. `0` is a real answer, not "unknown". */
  reactivatedComparisonCount: number | null;
  /** When the deck had been deleted; null when the column was never stamped. */
  deletedAt: string | null;
  /** The deleting analyst's RMS user id, matched against the signed-in user to render "you". */
  deletedByUserId: number | null;
  deletedByFirstName: string | null;
  deletedByLastName: string | null;
}


/**
 * The result of soft-deleting a slide deck through `DELETE /rms/slide-decks/{id}` (RA-2703).
 *
 * Field names match `SlideDeckDeleteResultResource` exactly. These are the backend's extracted **camelCase
 * scalar columns**, not the verbatim snake_case payload — the no-snake-to-camel-mapper rule applies only to
 * `ComparisonPayload`.
 */
export interface SlideDeckDeleteResult {
  /** The soft-deleted deck's row id. */
  deckId: number | null;
  /** The producer's content-hash business key, for support and logs. */
  slideDeckId: string | null;
  /**
   * The comparisons the **server** deactivated. The success note quotes this length, never the count the
   * confirm dialog predicted: that came from a snapshot a colleague's ingest can invalidate between render
   * and click.
   */
  deactivatedComparisonIds: Array<number> | null;
  /** The in-flight comparison requests this deletion cancelled. */
  failedComparisonRequestIds: Array<number> | null;
  /**
   * Whether TARA acknowledged the deletion. **Never surfaced to the analyst** — RMS's delete stands either
   * way, and the backend's error marker is the channel for this. A `console.warn` is acceptable; a banner is
   * not.
   */
  taraNotified: boolean | null;
}
